import { observer } from "mobx-react-lite";

import { Heading, IDialogProps } from "@bps/fluent-ui";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { DialogWithSubmitButtonsProps } from "@ui-components/form/submission-form-dialog/DialogWithSubmitButtons.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";

import { QuickAddContactFormFields } from "./QuickAddContactFormFields.tsx";
import { QuickAddContactFormValidator } from "./QuickAddContactFormValidator.tsx";
import { QuickAddContactFormValues } from "./QuickAddContactFormValues.tsx";
import {
  addContactByType,
  getQuickAddInitialValues,
  getQuickAddTitle
} from "./utils.ts";

type QuickAddContactDialogProps = Pick<IDialogProps, "hidden"> &
  Pick<DialogWithSubmitButtonsProps["dialogProps"], "onDismiss"> & {
    type: ContactType;
    initialValues?: QuickAddContactFormValues;
    onContactAdded: (contact: Contact) => void;
    detailedView?: boolean;
    title?: string;
  };

export const QuickAddContactDialog: React.FunctionComponent<QuickAddContactDialogProps> =
  observer(
    ({
      type,
      initialValues: defaultValues,
      onContactAdded,
      hidden,
      onDismiss,
      detailedView,
      title
    }) => {
      const root = useStores();

      const { core, userExperience } = root;

      if (hidden) return null;

      let newContact: Contact;

      const { localisedConfig } = userExperience;

      const patientLabel = localisedConfig("patientDisplay");
      const validator = new QuickAddContactFormValidator(
        {
          contactType: type
        },
        core.ref.countries.values,
        core.tenantDetails?.country!
      );

      return (
        <SubmissionFormDialog<QuickAddContactFormValues>
          dialogName="Quick add contact dialog"
          initialValues={getQuickAddInitialValues(
            core.tenantDetails?.country,
            type,
            {
              defaultValues,
              detailedView
            }
          )}
          validate={values => validator.validate(values)}
          onSubmit={async (values: QuickAddContactFormValues) => {
            newContact = await addContactByType(values, type, root);
          }}
          buttonsProps={{
            hideButtonsSeparator: true,
            disableSubmitOnPristine:
              !defaultValues?.firstName?.trim() &&
              !defaultValues?.lastName?.trim()
          }}
          onSubmitSucceeded={() => {
            if (!newContact) {
              return;
            }
            onContactAdded(newContact);
          }}
          subscription={{ values: true }}
          dialogProps={{
            maxWidth: 480,
            minWidth: 480,
            onDismiss,
            modalProps: {
              containerClassName: "quick-add-modal-container",
              layerProps: { eventBubblingEnabled: true },
              className: "quick-add-modal"
            },
            dialogContentProps: {
              title: (
                <Heading variant="modal-heading">
                  {getQuickAddTitle(title, type, patientLabel)}
                </Heading>
              ),
              showCloseButton: true
            }
          }}
        >
          {() => (
            <QuickAddContactFormFields
              contactType={type}
              detailedView={detailedView}
            />
          )}
        </SubmissionFormDialog>
      );
    }
  );
